import {
  EMPTY_OBJECT,
  NotificationVariants,
  useConstant,
  useGlobalToasts,
  type Basket,
  type ColumnDef,
  type DialogProps,
  type MinimalSubscriptionResponse,
} from '@talos/kyoko';
import { parse } from 'date-fns';
import { compact, isString } from 'lodash-es';
import Papa from 'papaparse';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { BehaviorSubject } from 'rxjs';
import { ImportDialog, type ImportStep } from '../../../../components/ImportDialog';
import { convertDateToDefaultCareOrderUTCDate } from '../../../../components/OMS/CareOrderFormView/utils';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import { useBaskets } from '../../../../providers/BasketsProvider';

interface BasketImportRow {
  ETFTradingTicker: string;
  BasketTradeDate: string;
  SecurityDescription: string;
  BasketQuantity: string;
  BasketPurpose: 'Creation' | 'Redemption' | 'Standard' | 'Pricing';
  TotalNumberofIncludedConstituents: string; // eg "1" for GBTC, "5" for GDLC

  symbol?: string; // talos symbol
  warning: string | undefined;
}

/** Converts a  m/d/y boomer date into a UTC ISO-8601 timestamp at noon UTC on that day */
function basketTradeDateToBasketRatioDate(dateString: string): string | undefined {
  try {
    const localDate = parse(dateString, 'M/d/yyyy', new Date());
    const standardizedDate = convertDateToDefaultCareOrderUTCDate(localDate);
    const isoString = standardizedDate.toISOString();
    return isoString;
  } catch (e) {
    return undefined;
  }
}

export const BasketDetailsImportDialog = (props: DialogProps) => {
  const [step, setStep] = useState<ImportStep>('initial');
  const [loading, setLoading] = useState(false);
  const { add: addToast } = useGlobalToasts();
  const { basketImportUnderlyingSecurityMapping = EMPTY_OBJECT } = useFeatureFlag();

  const subject = useConstant(
    new BehaviorSubject<MinimalSubscriptionResponse<BasketImportRow>>({ initial: true, data: [] })
  );
  const dataObservable = useMemo(() => subject.asObservable(), [subject]);

  const basketImportToData = useCallback(
    (rows: BasketImportRow[]): Basket[] => {
      return compact(
        rows.map(row => {
          if (!row.symbol) {
            addToast({
              text: `Unsupported Security Description: ${row.SecurityDescription}`,
              variant: 'NEGATIVE',
            });
            return undefined;
          }

          const dateString = basketTradeDateToBasketRatioDate(row.BasketTradeDate);
          if (!dateString) {
            addToast({
              text: `Unable to parse Basket Trade Date: ${row.BasketTradeDate}`,
              variant: 'NEGATIVE',
            });
            return undefined;
          }

          return {
            BasketSymbol: row.ETFTradingTicker,
            UnderlyingSecuritySymbol: row.symbol,
            QuantityDetails: {
              BasketRatioDate: dateString,
              BasketRatio: row.BasketQuantity,
            },
          };
        })
      );
    },
    [addToast]
  );

  useEffect(() => {
    if (props.isOpen) {
      setStep('initial');
      subject.next({ initial: true, data: [] });
    }
  }, [props.isOpen, subject]);

  const readFile = useCallback(
    (file: File) => {
      return new Promise((resolve: (value: BasketImportRow[]) => void, reject: (reason: string) => void) => {
        try {
          Papa.parse<BasketImportRow>(file, {
            delimiter: ',',
            header: true,
            skipEmptyLines: true,
            transformHeader: (header: string) => {
              return header.replaceAll(' ', '').trim();
            },
            complete: result => {
              const validation = validateRows(result);
              if (!validation.ok) {
                reject(validation.error);
                return;
              }

              const relevantRows = result.data.filter(
                row => row.BasketPurpose === 'Standard' && row.TotalNumberofIncludedConstituents === '1'
              );

              resolve(
                relevantRows.map(row => ({
                  // Just pick out the stuff we care about
                  ETFTradingTicker: row.ETFTradingTicker,
                  BasketTradeDate: row.BasketTradeDate,
                  SecurityDescription: row.SecurityDescription,
                  BasketQuantity: row.BasketQuantity,
                  BasketPurpose: row.BasketPurpose,
                  TotalNumberofIncludedConstituents: row.TotalNumberofIncludedConstituents,
                  symbol: basketImportUnderlyingSecurityMapping[row.SecurityDescription],
                  warning: undefined,
                }))
              );
            },
          });
        } catch (e: unknown) {
          reject('Failed to parse file');
        }
      });
    },
    [basketImportUnderlyingSecurityMapping]
  );

  const handleUpload = useCallback(
    async (file: File | undefined) => {
      if (file == null) {
        return;
      }
      try {
        const data = await readFile(file);
        subject.next({ initial: true, data });
        setStep('preview');
      } catch (e) {
        setStep('initial');
        addToast({
          text: isString(e) ? e : 'Failed to parse file',
          variant: NotificationVariants.Negative,
        });
      }
    },
    [readFile, addToast, subject]
  );

  const { upsertBaskets } = useBaskets();

  const handleConfirm = useCallback(() => {
    setLoading(true);

    upsertBaskets(basketImportToData(subject.value.data))
      .then(() => {
        setStep('initial');
        subject.next({ initial: true, data: [] });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [subject, upsertBaskets, basketImportToData]);

  const rowCount = subject.value.data.length;

  return (
    <ImportDialog
      {...props}
      title="CSV Basket Details Import"
      step={step}
      dataObservable={dataObservable}
      columns={PREVIEW_COLUMNS}
      onConfirm={handleConfirm}
      cancelLabel="Close"
      confirmLoading={loading}
      confirmDisabled={step !== 'preview'}
      confirmLabel={`Import ${rowCount} row(s)`}
      rowID={'ETFTradingTicker' satisfies keyof BasketImportRow}
      onUpload={handleUpload}
      enableRowSelection={false}
    />
  );
};

const PREVIEW_COLUMNS: ColumnDef<BasketImportRow>[] = [
  {
    type: 'text',
    field: 'ETFTradingTicker',
    width: 150,
    sort: '+',
  },
  {
    type: 'security',
    field: 'symbol',
    width: 120,
  },
  {
    type: 'text',
    field: 'BasketTradeDate',
    width: 130,
  },
  {
    type: 'text',
    field: 'SecurityDescription',
  },
  {
    type: 'number',
    field: 'BasketQuantity',
  },
  {
    type: 'text',
    field: 'BasketPurpose',
    width: 130,
  },
];

type BasketImportRowProperty = keyof Omit<BasketImportRow, 'warning'>;
const KEYS: BasketImportRowProperty[] = [
  'BasketQuantity',
  'BasketTradeDate',
  'ETFTradingTicker',
  'SecurityDescription',
  'BasketPurpose',
  'TotalNumberofIncludedConstituents',
];

// Just some simple validation to catch malformed entry. We make sure all the data is here, thats all
function validateRows(result: Papa.ParseResult<BasketImportRow>): { ok: true } | { ok: false; error: string } {
  const papaError = result.errors.at(0)?.message;
  if (papaError) {
    return { ok: false, error: papaError };
  }

  // Validate that the parsing was ok
  for (let i = 0; i < result.data.length; i++) {
    const row = result.data[i];
    for (const key of KEYS) {
      if (row[key] == null) {
        return { ok: false, error: `Could not find ${key} at row ${i + 1}` };
      }
    }
  }

  return { ok: true };
}
